import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { generateUUID, isNotEmpty, i18nIntl } from '../../lib/ep.js';

import ActionButton from '../builder/ActionButton.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';
import Select from '../builder/Select.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout.js';
import CannonballClient from '../front/CannonballClient.js';
import BasketballClient from '../front/BasketballClient.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';
import ColorPicker from '../builder/ColorPicker.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import DynamicFields from '../builder/DynamicFields.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import Message from '../builder/Message.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input.js';

const TargetField = ({ model, change, ...props }) => {
	const [ uploading, setUploading ] = useState(false);

	const auto_img_hooks = (hook) => {
		switch(hook.type) {
			case 'AUTO_IMAGE_START':
				setUploading(true);
				break;

			case 'AUTO_IMAGE_END':
				setUploading(false);
				break;
		}
	}

	return (
		<Model
			model={model}
			change={change}
			errors={props.errors != undefined && props.errors[model.id]}
		>
			<Input
				name='title'
				max_length={15}
				_class={{ size:60 }}
				placeholder="Target"
			>Title</Input>

			<Checkbox
				name="double_points"
			>Does it give the double of points on hit?</Checkbox>

			<AutoPostImageUploader
				auto_url={`/promotions/auto_img/${props.promotion_id}`}
				auto_type='stages'
				changeMulti={props.changeMulti}
				hooks={auto_img_hooks}
				image={model.full_image}
				name="image"
				small_input={true}
				_size={{ width:150, height:150 }}
				_class={{ size:50 }}
			>Image</AutoPostImageUploader>

			<AutoPostImageUploader
				auto_url={`/promotions/auto_img/${props.promotion_id}`}
				auto_type='stages'
				changeMulti={props.changeMulti}
				hooks={auto_img_hooks}
				image={model.full_hit_image}
				name="hit_image"
				small_input={true}
				_size={{ width:150, height:150 }}
				_class={{ size:50 }}
			>Hitted image</AutoPostImageUploader>
		</Model>
	)
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors_game, setErrorsGame ] = useState({});

	const option_positioning = [
		{ k:'fixed', v:i18n("Fixed") },
		{ k:'random', v:i18n("Random") },
	];

	const option_positioning_target = [
		{ k:'fixed', v:i18n("Fixed") },
		{ k:'random', v:i18n("Random") },
		{ k:'semi-moving', v:i18n("Moving after a delay") },
		{ k:'moving', v:i18n("Constantly moving") },
	];

	const option_positioning_obstacle = [
		{ k:'fixed', v:i18n("Fixed") },
		{ k:'random', v:i18n("Random") },
		{ k:'moving', v:i18n("Constantly moving") },
	];

	const option_obstacle = [
		{ k:'none', v:i18n("None") },
		{ k:'normal', v:i18n("Block from the floor") },
		{ k:'hole', v:i18n("Pass through the hole") },
	];

	const option_hole_size = [
		{ k:'small', v:i18n("Small") },
		{ k:'normal', v:i18n("Normal") },
		{ k:'big', v:i18n("Big") },
		{ k:'huge', v:i18n("Huge") },
	];

	const option_shot_path = [
		{ k:'full', v:i18n("Full") },
		{ k:'normal', v:i18n("***Normal") },
		{ k:'short', v:i18n("Short") },
		{ k:'none', v:i18n("None") },
	];

	const option_points_for_fail = [{ k:'0', v:"Score is not deduced" }];
	const points_for_fail_mod = 5;
	[...Array(5).keys()].map((foo, i) => {
		const p = points_for_fail_mod * (i + 1);
		option_points_for_fail.push({ k:p, v:`${props.intl.formatMessage({ id:"Deduce {total} points" }, { total:p })}` });
	});

	const dynamic_fields = [{
		type:'target',
		label:"Target",
		icon:'knk-flag',
		translate:[
			{key:'title',type:'input'},
		],
		title:(f) => {
			if(isNotEmpty(f, 'title')) return f.title;
			return i18n("Target");
		},
		max:5,
	}];

	const targets = stage?.cannonball_targets ?? [];
	if(targets.length == 0) {
		targets.push({
			id:generateUUID(),
			type:'target',
			title:i18n("Target"),
			double_points:false,
			can_delete:false,
			can_copy:false,
		});
	}

	const max_targets = parseInt(stage?.cannonball_num_hits ?? '5');

	const section_title = `${i18n("Targets")} (${targets.length}/${max_targets})`;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	const type = props?.type ?? 'cannonball';

	const Client = (type == 'basketball') ? BasketballClient : CannonballClient;

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
				errors={(Object.keys(errors_game).length > 0) ? true : false}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Range
							min={30}
							max={300}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							name="game_time"
							value={Math.max((stage?.game_time ?? 60), 30)}
							_class={{ first:true }}
						>Time limit</Range>

						<Range
							min={1}
							max={10}
							step={1}
							name="cannonball_num_hits"
							value={Math.max(max_targets, 1)}
							_class={{ size:60 }}
						>Number of goals</Range>

						<Select
							name="cannonball_points_for_fail"
							values={option_points_for_fail}
							value={parseInt(stage?.cannonball_points_for_fail ?? '0')}
							_class={{ size:50 }}
						>If shot is missed...</Select>
					</Model>
				</article>

				{(type == 'basketball') && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Target"
							icon='knk-flag'
						/>

						<Model model={stage} change={change}>
							<Select
								name="cannonball_relocate_target"
								values={option_positioning_target}
								value={stage?.cannonball_relocate_target ?? 'random'}
								_class={{ size:50 }}
							>Position</Select>
						</Model>
					</article>
				)}

				{(type == 'cannonball') && (
					<article class="gui_not_in_top_huge">
						<DynamicFields
							section_title={section_title}
							section_icon='knk-flag'
							onChange={(v) => change('cannonball_targets', v)}
							field_types={dynamic_fields}
							has_translation={true}
							locales={avail_locales}
							add_button_props={{
								icon:'knk-add-item mainButton',
							}}
							sortable={false}
							min_items={1}
							max_items={max_targets}
							fields={targets}
						>
							<TargetField
								promotion_id={stage.promotion_id}
								intl={props.intl}
							/>
						</DynamicFields>

						<Model model={stage} change={change}>
							<Select
								name="cannonball_relocate_target"
								values={option_positioning_target}
								value={stage?.cannonball_relocate_target ?? 'random'}
								_class={{ size:50 }}
							>Position</Select>
						</Model>
					</article>
				)}

				{(type == 'cannonball') && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Obstacle"
							icon='knk-corporate'
						/>

						<Model model={stage} change={change}>
							<Select
								name="cannonball_obstacle_type"
								values={option_obstacle}
								value={stage?.cannonball_obstacle_type ?? 'none'}
								_class={{ size:50 }}
							>Type</Select>
						</Model>

						{((stage?.cannonball_obstacle_type ?? 'none') != 'none') && (
							<Model model={stage} change={change}>
								{(stage.cannonball_obstacle_type != 'hole') && (
									<Select
										name="cannonball_relocate_obstacle"
										values={option_positioning_obstacle}
										value={stage?.cannonball_relocate_obstacle ?? 'fixed'}
										_class={{ size:50 }}
									>Position</Select>
								)}

								{(stage.cannonball_obstacle_type == 'hole') && (
									<Select
										name="cannonball_obstacle_hole_size"
										values={option_hole_size}
										value={stage?.cannonball_obstacle_hole_size ?? 'normal'}
										_class={{ size:50 }}
									>Hole size</Select>
								)}

								{(stage.cannonball_obstacle_type == 'hole') && (
									<AutoPostImageUploader
										auto_url={`/promotions/auto_img/${stage.promotion_id}`}
										auto_type='stages'
										image={stage.full_cannonball_obstacle_top_image}
										name="cannonball_obstacle_top_image"
										small_input={true}
										_size={{ width:115, height:600 }}
										_class={{ size:50 }}
									>Top image</AutoPostImageUploader>
								)}

								<AutoPostImageUploader
									auto_url={`/promotions/auto_img/${stage.promotion_id}`}
									auto_type='stages'
									image={stage.full_cannonball_obstacle_bottom_image}
									name="cannonball_obstacle_bottom_image"
									small_input={true}
									_size={{ width:115, height:600 }}
									_class={{ size:50 }}
								>{(stage.cannonball_obstacle_type != 'hole') ? "Image" : "Bottom image"}</AutoPostImageUploader>
							</Model>
						)}
					</article>
				)}

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Scorer"
						icon='knk-focus-point'
					/>

					<Model model={stage} change={change}>
						<Select
							name="cannonball_relocate_ball"
							values={option_positioning}
							value={stage?.cannonball_relocate_ball ?? 'fixed'}
							_class={{ size:50 }}
						>Position</Select>

						<Select
							name="game_display_path"
							values={option_shot_path}
							value={stage?.game_display_path ?? 'full'}
							_class={{ size:50 }}
						>Display trajectory</Select>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors_game).length == 0) ? (
							<Client
								stage={{ Stage:stage }}
								is_builder={true}
								is_playable={true}
								section="settings"
							/>
						) : (
							<GameContainerWrapper>
								<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
									{Object.values(errors_game).map((error, i) => {
										if(typeof error === 'object') return null;

										return (
											<p key={`error${i}`}>{error}</p>
										)
									})}
								</div>
							</GameContainerWrapper>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const Client = ((props?.type ?? 'cannonball') == 'basketball') ? BasketballClient : CannonballClient;

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<BuilderSubSectionTitle
						title="Field"
						icon='knk-football-play-field'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${stage.promotion_id}`}
							auto_type='stages'
							image={stage.full_bg_image}
							name="bg_image"
							small_input={true}
							_size={{ width:800, height:1000 }}
						>Background image</AutoPostImageUploader>

						<ColorPicker
							name='bg_color'
							color={stage?.bg_color ?? 'rgba(80, 190, 195, 1)'}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='shooter_color'
							color={stage?.shooter_color ?? 'rgba(255, 255, 255, 1)'}
							_class={{ size:50 }}
						>Scorer color</ColorPicker>

						<Range
							min={70}
							max={105}
							step={5}
							name="cannonball_floor_y"
							value={Math.max(Math.min((parseInt(stage?.cannonball_floor_y ?? '85')), 105), 70)}
							_class={{ size:60 }}
						>Floor level</Range>
					</Model>

					{(parseInt(stage?.cannonball_floor_y ?? '80') > 100)  && (
						<Message _class={{ other:'in_bottom' }}>
							<p>{i18n("The ball will disappear underneath the bottom edge of the stage")}</p>
						</Message>
					)}
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Sound FX"
						icon='knk-music'
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<Client
							stage={{ Stage:stage }}
							type={props.type}
							is_builder={true}
							is_playable={true}
							section="design"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	const Client = ((props?.type ?? 'cannonball') == 'basketball') ? BasketballClient : CannonballClient;

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<Client
							stage={{ Stage:stage }}
							type={props.type}
							is_builder={true}
							is_playable={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const CannonballBuilder = ({ base_route, ...props }) => {
	const type = props?.type ?? 'cannonball';
	const basename = `/stages/${props.stage.id}/${type}`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const stage = props.stage;

	const common_props = {
		stage,
		type,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	const Client = ((props?.type ?? 'cannonball') == 'basketball') ? BasketballClient : CannonballClient;

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<Client
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['fail', 'out_of_moves', 'out_of_time']}
						>
							<Client
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(CannonballBuilder);